// Tarefas recorrentes (US-095 / US-097): o molde, os dias em que acontece e a validação.
// As mesmas regras estão no banco (0008/0009 — recurring_occurs / recurring_sync).
import { addDays, isValidDateKey, normalizeTime, parseDateKey, toDateKey, type DateKey } from './dates';
import { validateTaskInput, type TaskError } from './task';

export interface RecurringTask {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  due_time: string | null; // HH:MM
  estimate_minutes: number | null;
  /** 0 = domingo … 6 = sábado */
  weekdays: number[];
  /** dias do mês (1 a 31); o que o mês não tem cai no último dia (ASM-111) */
  month_days: number[];
  /** os dias do mês valem todos os meses (1) ou a cada 2, 3 ou 6 meses (AC-224) */
  month_interval: MonthInterval;
  /** 1º dia do mês em que a contagem começa (só com período maior que 1 — ASM-119) */
  month_start: DateKey | null;
  created_at: string;
  updated_at: string;
}

/** Todos os meses · bimestral · trimestral · semestral */
export const MONTH_INTERVALS = [1, 2, 3, 6] as const;
export type MonthInterval = (typeof MONTH_INTERVALS)[number];

export interface RecurringFields {
  title: string;
  description: string | null;
  due_time: string | null;
  estimate_minutes: number | null;
  weekdays: number[];
  month_days: number[];
  month_interval: MonthInterval;
  month_start: DateKey | null;
}

type Schedule = Pick<RecurringTask, 'weekdays' | 'month_days'> & Partial<Pick<RecurringTask, 'month_interval' | 'month_start'>>;

export type RecurringField = keyof RecurringFields | 'days';
export type RecurringError = TaskError | 'days_required';

/** Ordem dos botões: Seg · Ter · Qua · Qui · Sex · Sáb · Dom (ASM-110). */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** As ocorrências aparecem de hoje até 7 dias à frente (ASM-108). */
export const RECURRING_HORIZON_DAYS = 7;

export interface RecurringInput {
  title: string;
  description?: string | null;
  due_time?: string | null;
  estimate_minutes?: number | null;
  weekdays: number[];
  month_days: number[];
  month_interval?: number;
  month_start?: DateKey | null;
}

export function validateRecurringInput(
  input: RecurringInput
): { ok: true; value: RecurringFields } | { ok: false; errors: Partial<Record<RecurringField, RecurringError>> } {
  // título, descrição e duração seguem as regras da tarefa
  const base = validateTaskInput({ title: input.title, description: input.description, estimate_minutes: input.estimate_minutes });
  const errors: Partial<Record<RecurringField, RecurringError>> = base.ok ? {} : { ...base.errors };
  // na recorrente o horário não depende de data
  let due_time: string | null = null;
  if (input.due_time) {
    due_time = normalizeTime(input.due_time);
    if (!due_time) errors.due_time = 'invalid_time';
  }
  const weekdays = [...new Set(input.weekdays)].filter((d) => Number.isInteger(d) && d >= 0 && d <= 6).sort((a, b) => a - b);
  const month_days = [...new Set(input.month_days)].filter((d) => Number.isInteger(d) && d >= 1 && d <= 31).sort((a, b) => a - b);
  if (!weekdays.length && !month_days.length) errors.days = 'days_required';
  // o período só faz sentido com dias do mês; sem eles, fica "todos os meses"
  const interval = (MONTH_INTERVALS as readonly number[]).includes(input.month_interval ?? 1) ? ((input.month_interval ?? 1) as MonthInterval) : null;
  if (interval === null) errors.month_days = 'invalid_date';
  const month_interval: MonthInterval = month_days.length && interval ? interval : 1;
  const start = input.month_start ?? null;
  if (month_interval > 1 && !(start && isValidDateKey(start) && start.endsWith('-01'))) errors.month_days = 'invalid_date';
  if (!base.ok || Object.keys(errors).length) return { ok: false, errors };
  const { title, description, estimate_minutes } = base.value;
  return {
    ok: true,
    value: { title, description, due_time, estimate_minutes, weekdays, month_days, month_interval, month_start: month_interval > 1 ? start : null },
  };
}

const monthIndex = (d: Date) => d.getFullYear() * 12 + d.getMonth();
const lastDayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

/** O dia marcado cai neste dia do mês? (o que o mês não tem cai no último dia) */
function monthDayMatches(monthDays: number[], d: Date): boolean {
  const date = d.getDate();
  if (monthDays.includes(date)) return true;
  const last = lastDayOf(d);
  return date === last && monthDays.some((m) => m > last);
}

/**
 * Onde a contagem dos meses começa: o mês da próxima vez de algum dia marcado — este mês,
 * se o dia ainda não passou; senão, o seguinte (ASM-119). Devolve o 1º dia desse mês.
 */
export function firstMonthStart(monthDays: number[], today: DateKey): DateKey {
  const d = parseDateKey(today);
  const last = lastDayOf(d);
  const thisMonth = monthDays.some((m) => Math.min(m, last) >= d.getDate());
  return toDateKey(new Date(d.getFullYear(), d.getMonth() + (thisMonth ? 0 : 1), 1));
}

/**
 * O início da contagem ao salvar: mantém o anterior se os dias do mês e o período não
 * mudaram (ASM-120); senão, recomeça na próxima vez do dia.
 */
export function resolveMonthStart(
  next: { month_days: number[]; month_interval: MonthInterval },
  prev: Pick<RecurringTask, 'month_days' | 'month_interval' | 'month_start'> | null,
  today: DateKey
): DateKey | null {
  if (next.month_interval === 1 || !next.month_days.length) return null;
  const sorted = (a: number[]) => [...a].sort((x, y) => x - y).join(',');
  const same = (a: number[], b: number[]) => sorted(a) === sorted(b);
  if (prev?.month_start && prev.month_interval === next.month_interval && same(prev.month_days, next.month_days)) return prev.month_start;
  return firstMonthStart(next.month_days, today);
}

/**
 * A recorrente acontece neste dia? Dia da semana marcado, dia do mês marcado ou — no último
 * dia do mês — algum dia marcado que esse mês não tem (31 em abril cai no dia 30).
 */
export function occursOn(rec: Schedule, day: DateKey): boolean {
  const d = parseDateKey(day);
  if (rec.weekdays.includes(d.getDay())) return true;
  if (!monthDayMatches(rec.month_days, d)) return false;
  // bimestral/trimestral/semestral: só nos meses do ciclo, a partir do início (AC-224)
  const interval = rec.month_interval ?? 1;
  if (interval === 1) return true;
  if (!rec.month_start) return false;
  const months = monthIndex(d) - monthIndex(parseDateKey(rec.month_start));
  return months >= 0 && months % interval === 0;
}

/** Dias com ocorrência de hoje até 7 dias à frente. */
export function upcomingOccurrences(rec: Schedule, today: DateKey): DateKey[] {
  const days: DateKey[] = [];
  for (let i = 0; i <= RECURRING_HORIZON_DAYS; i++) {
    const day = addDays(today, i);
    if (occursOn(rec, day)) days.push(day);
  }
  return days;
}
